// src/pages/reports/RuahTermComparison.tsx
//
// Daily Organiser report: compares RUAH minutes per teacher across two
// terms (T1↔T2, T2↔T3, or T3↔T4). All math lives in
// dailyOrganiserReportsService; this file is presentation only.

import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import { TableSkeleton } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../contexts/ToastContext';
import { useData } from '../../contexts/DataContext';
import { gridsService } from '../../services/gridsService';
import { getLoadLetterSettings } from '../../services/loadLetterService';
import {
  getRuahTermComparison,
  type RuahComparisonRow,
  type RuahTerm,
} from '../../services/dailyOrganiserReportsService';
import { exportReport, Formatters, type ExportData } from '../../utils/reportExport';
import { displayLearningArea } from '../../constants/learningAreas';

type Comparison = 't1-t2' | 't2-t3' | 't3-t4';

const COMPARISON_TERMS: Record<Comparison, { a: RuahTerm; b: RuahTerm; labelA: string; labelB: string }> = {
  't1-t2': { a: 'term1', b: 'term2', labelA: 'Term 1', labelB: 'Term 2' },
  't2-t3': { a: 'term2', b: 'term3', labelA: 'Term 2', labelB: 'Term 3' },
  't3-t4': { a: 'term3', b: 'term4', labelA: 'Term 3', labelB: 'Term 4' },
};

const RuahTermComparison: React.FC = () => {
  const { addToast } = useToast();
  const { departments } = useData();

  const [years, setYears] = useState<number[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [comparison, setComparison] = useState<Comparison>('t1-t2');
  const [department, setDepartment] = useState<string>('all');
  const [activeOnly, setActiveOnly] = useState(true);
  const [rows, setRows] = useState<RuahComparisonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState<keyof RuahComparisonRow>('deltaMinutes');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Load year options and default year on mount.
  useEffect(() => {
    (async () => {
      try {
        const [available, settings] = await Promise.all([
          gridsService.getAvailableYears(),
          getLoadLetterSettings(),
        ]);
        setYears(available);
        const defaultYear = settings?.currentAcademicYear
          ? Number(settings.currentAcademicYear)
          : available[0] ?? new Date().getFullYear();
        setYear(defaultYear);
      } catch (err) {
        console.error('Failed to load year options:', err);
        addToast('Could not load year options', 'error');
      }
    })();
  }, [addToast]);

  // Recompute whenever inputs change.
  useEffect(() => {
    if (year == null) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await getRuahTermComparison({
          year,
          termA: COMPARISON_TERMS[comparison].a,
          termB: COMPARISON_TERMS[comparison].b,
          department,
          activeOnly,
        });
        if (!cancelled) setRows(result);
      } catch (err) {
        console.error('RUAH comparison failed:', err);
        if (!cancelled) addToast('Failed to generate report', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, comparison, department, activeOnly, addToast]);

  const labels = COMPARISON_TERMS[comparison];

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      // For deltaMinutes default, sort by absolute value.
      if (sortKey === 'deltaMinutes') {
        const diff = Math.abs(bv as number) - Math.abs(av as number);
        return sortDir === 'desc' ? diff : -diff;
      }

      // Push null values to the end regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'desc' ? bv - av : av - bv;
      }
      const as = String(av);
      const bs = String(bv);
      return sortDir === 'desc' ? bs.localeCompare(as) : as.localeCompare(bs);
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const totals = useMemo(
    () =>
      sortedRows.reduce(
        (acc, r) => {
          acc.termAMinutes += r.termAMinutes;
          acc.termBMinutes += r.termBMinutes;
          return acc;
        },
        { termAMinutes: 0, termBMinutes: 0 }
      ),
    [sortedRows]
  );
  const totalDelta = totals.termBMinutes - totals.termAMinutes;

  const handleSort = (key: keyof RuahComparisonRow) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    const data: ExportData = {
      title: `RUAH Term Comparison — ${labels.labelA} vs ${labels.labelB}`,
      description: `Per-teacher RUAH minutes for ${year}`,
      metadata: {
        'Generated': new Date().toLocaleDateString(),
        'Year': String(year),
        'Comparison': `${labels.labelA} vs ${labels.labelB}`,
        'Learning Area': department === 'all' ? 'All Learning Areas' : department,
        'Teachers': String(sortedRows.length),
      },
      columns: [
        { header: 'Teacher', key: 'staffName' },
        { header: 'Code', key: 'staffCode' },
        { header: 'Learning Area', key: 'department' },
        { header: 'FTE', key: 'fte', format: (v) => Formatters.decimal(v, 1) },
        {
          header: `${labels.labelA} subject`,
          key: 'termASubjects',
          format: (v) => (Array.isArray(v) ? v.join(', ') : ''),
        },
        { header: `${labels.labelA} (mins)`, key: 'termAMinutes' },
        {
          header: `${labels.labelB} subject`,
          key: 'termBSubjects',
          format: (v) => (Array.isArray(v) ? v.join(', ') : ''),
        },
        { header: `${labels.labelB} (mins)`, key: 'termBMinutes' },
        { header: 'Δ Minutes', key: 'deltaMinutes' },
        {
          header: 'Δ %',
          key: 'deltaPercent',
          format: (v) => (v == null ? '—' : `${(v as number).toFixed(1)}%`),
        },
      ],
      rows: sortedRows,
    };
    exportReport(data, format);
    addToast(`Report exported as ${format.toUpperCase()}`, 'success');
  };

  const deltaClass = (delta: number) =>
    delta > 0 ? 'text-ok-fg' : delta < 0 ? 'text-danger-fg' : 'text-ink-500';

  return (
    <div>
      <PageHeader
        title="RUAH Term Comparison"
        description="Which teachers pick up or drop RUAH minutes between two terms, from the released load letters"
        actions={
          <>
            <select
              value={year ?? ''}
              onChange={e => setYear(Number(e.target.value))}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={comparison}
              onChange={e => setComparison(e.target.value as Comparison)}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              <option value="t1-t2">Term 1 vs Term 2</option>
              <option value="t2-t3">Term 2 vs Term 3</option>
              <option value="t3-t4">Term 3 vs Term 4</option>
            </select>
            <select
              value={department}
              onChange={e => setDepartment(e.target.value)}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              <option value="all">All Learning Areas</option>
              {departments.map(d => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </select>
            <label className="inline-flex items-center text-body-sm text-ink-700 gap-2 px-2">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={e => setActiveOnly(e.target.checked)}
              />
              Active staff only
            </label>
            <div className="relative">
              <select
                onChange={e => {
                  if (e.target.value) {
                    handleExport(e.target.value as 'csv' | 'excel' | 'pdf');
                    e.target.value = '';
                  }
                }}
                className="border border-ink-300 rounded-md px-3 py-2 bg-white text-body-sm"
                defaultValue=""
              >
                <option value="" disabled>Export</option>
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </>
        }
      />

      <Card>
        <p className="text-meta text-ink-500 mb-4">
          <strong>RUAH</strong> is the College's term-by-term elective/enrichment
          rotation, timetabled separately from mainstream classes. This report reads
          each teacher's RUAH assignments from the released load letters and shows the
          RUAH minutes per fortnight they carry in {labels.labelA} against {labels.labelB},
          so the Daily Organiser can see who gains or loses RUAH load as the rotation
          turns over. <strong className="text-ok-fg">Positive Δ</strong> means more RUAH
          in {labels.labelB}; <strong className="text-danger-fg">negative Δ</strong> means
          less. Teachers with no RUAH in either term are hidden.
        </p>

        {loading ? (
          <TableSkeleton rows={6} cols={9} />
        ) : sortedRows.length === 0 ? (
          <EmptyState
            icon={<ArrowLeftRight className="h-6 w-6" />}
            title={`No RUAH data for ${year}`}
            description="Check that a RUAH grid exists for this year and contains assignments, or choose a different year above."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-200">
              <thead className="bg-ink-50">
                <tr>
                  <Th label="Teacher" onClick={() => handleSort('staffName')} />
                  <Th label="Learning Area" onClick={() => handleSort('department')} />
                  <Th label="FTE" onClick={() => handleSort('fte')} />
                  <Th label={`${labels.labelA} subject`} onClick={() => handleSort('termASubjects')} />
                  <Th label={`${labels.labelA} mins`} onClick={() => handleSort('termAMinutes')} />
                  <Th label={`${labels.labelB} subject`} onClick={() => handleSort('termBSubjects')} />
                  <Th label={`${labels.labelB} mins`} onClick={() => handleSort('termBMinutes')} />
                  <Th label="Δ mins" onClick={() => handleSort('deltaMinutes')} />
                  <Th label="Δ %" onClick={() => handleSort('deltaPercent')} />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-200">
                {sortedRows.map(r => (
                  <tr key={r.staffId} className="hover:bg-ink-50">
                    <td className="px-4 py-2 text-body-sm text-ink-900">
                      {r.staffName} <span className="text-ink-400 font-mono tabular-nums">({r.staffCode})</span>
                    </td>
                    <td className="px-4 py-2 text-body-sm text-ink-500">{displayLearningArea(r.department)}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-500 font-mono tabular-nums">{r.fte.toFixed(1)}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-700">
                      {r.termASubjects.length === 0
                        ? <span className="text-ink-400">—</span>
                        : r.termASubjects.join(', ')}
                    </td>
                    <td className="px-4 py-2 text-body-sm text-ink-700 font-mono tabular-nums">{r.termAMinutes}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-700">
                      {r.termBSubjects.length === 0
                        ? <span className="text-ink-400">—</span>
                        : r.termBSubjects.join(', ')}
                    </td>
                    <td className="px-4 py-2 text-body-sm text-ink-700 font-mono tabular-nums">{r.termBMinutes}</td>
                    <td className={`px-4 py-2 text-body-sm font-medium font-mono tabular-nums ${deltaClass(r.deltaMinutes)}`}>
                      {r.deltaMinutes > 0 ? '+' : ''}{r.deltaMinutes}
                    </td>
                    <td className={`px-4 py-2 text-body-sm font-mono tabular-nums ${r.deltaPercent == null ? 'text-ink-500' : deltaClass(r.deltaMinutes)}`}>
                      {r.deltaPercent == null
                        ? '—'
                        : `${r.deltaPercent > 0 ? '+' : ''}${r.deltaPercent.toFixed(1)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-ink-50 border-t-2 border-ink-200">
                <tr>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900" colSpan={4}>
                    Total ({sortedRows.length} teacher{sortedRows.length === 1 ? '' : 's'})
                  </td>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                    {totals.termAMinutes}
                  </td>
                  <td className="px-4 py-2" />
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                    {totals.termBMinutes}
                  </td>
                  <td className={`px-4 py-2 text-body-sm font-medium font-mono tabular-nums ${deltaClass(totalDelta)}`}>
                    {totalDelta > 0 ? '+' : ''}{totalDelta}
                  </td>
                  <td className="px-4 py-2" />
                </tr>
              </tfoot>
            </table>
            <div className="mt-3 text-meta text-ink-500">
              {sortedRows.length} teacher{sortedRows.length === 1 ? '' : 's'} with RUAH in{' '}
              {labels.labelA} or {labels.labelB}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const Th: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <th
    onClick={onClick}
    className="px-4 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer select-none"
  >
    {label}
  </th>
);

export default RuahTermComparison;
